'use client'

import type { ReactNode } from 'react'
import { useCallback } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import { ArrowIcon } from '@/components/icons/ArrowIcon'

interface FeaturedCardProps {
  title: string
  href: string
  variant?: 'light' | 'dark'
  topContent?: ReactNode
}

export function FeaturedCard({ title, href, variant = 'light', topContent }: FeaturedCardProps) {
  const router = useRouter()
  const isDark = variant === 'dark'

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      if (window.getSelection()?.toString()) return
      if ((e.target as HTMLElement).closest('a')) return
      router.push(href)
    },
    [href, router],
  )

  return (
    <div
      onClick={handleCardClick}
      className={`group flex flex-col justify-between transition-colors duration-300 h-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 ${
        isDark
          ? 'p-4 md:min-h-[240px]'
          : 'rounded-[12px] border border-black p-6 hover:bg-black lg:min-h-[320px]'
      }`}
    >
      {/* Top: optional content + title */}
      <div className="flex flex-col gap-3">
        {topContent}

        <h3
          className={`subheading-large xl:subheading-xlarge transition-colors duration-300 line-clamp-3 ${
            isDark ? 'text-white' : 'text-black group-hover:text-white'
          }`}
        >
          <Link href={href} className="text-inherit no-underline">
            {title}
          </Link>
        </h3>
      </div>

      {/* Bottom: read more CTA */}
      <div className="flex justify-end mt-6">
        <span
          className={`subheading-xsmall inline-flex items-center gap-1 transition-colors duration-300 underline underline-offset-4 ${
            isDark ? 'text-white' : 'text-black group-hover:text-white'
          }`}
        >
          Read more
          <ArrowIcon className="shrink-0 size-3 transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
        </span>
      </div>
    </div>
  )
}
